/**
 * Soft≠Live · Ops Now desk — Page220 Soft≠Live (Page176 residual · Instrument Theatre).
 * Mount: /ops
 * Buses: GET /ops/overview/status · devices · alarms · admin shots (gated).
 *
 * One job: 15-second glance → next move.
 *   pulse · KPI · focus (1 CTA) · space chips · unit chips
 * Soft theatre stays Live-gated honesty. No dual-plane engineer wall.
 * Honesty: Volumes · Movers · Open Plant · Alarms (painted Watch) · Site quiet
 */
import { useEffect, useMemo, useState } from "react";
import { html, Ico, Alert, go, M, SOFT, SPRING } from "./ui.js";
import { path, useApi } from "./api.js";
import { useLive, UNKNOWN } from "./live.js";
import { useDecisionHome, actionLabel } from "./ai-context-rail.js";
import { PathsealPanel, isLocalHost } from "./pathseal.js";
import { softZoneCatalogOf, loadZoneCamBinds } from "./ops-zone-fabric.js";
import { formatUserInstant } from "./time.js";
import { boardHeadingOf, cameraPlantSay, operatorSay, boardLabelSay, looksLikeCode } from "./operator-say.js";
import { TonightWatchCmc } from "./tonight-watch-cmc.js";

/** Soft≠Live: missing contract ops must not crash the desk. */
function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_overview_status") return "/ops/overview/status";
    if (op === "get__devices") return "/devices";
    if (op === "get__alarms") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/alarms?${parts.join("&")}` : "/alarms";
    }
    if (op === "get__admin_camera-snapshots") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length
        ? `/admin/camera-snapshots?${parts.join("&")}`
        : "/admin/camera-snapshots";
    }
    return "";
  }
}

/** Pure: seat chips from Live posture (Page220 dual survey ∥ admin). */
export function opsOverviewSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const dMax = Number(lim.device_paint_max);
  const aMax = Number(lim.alarm_paint_max);
  const surveyAllowed = (p.survey_plane_allowed === true || p.register_allowed === true);
  const adminAllowed = (p.admin_plane_allowed === true || p.admin_snapshots_allowed === true);
  const registerAllowed = (p.register_allowed === true);
  return {
    surveyAllowed,
    adminAllowed,
    registerAllowed,
    alarmsAllowed: (p.alarms_read_allowed === true),
    homeContextAllowed: (p.home_context_allowed === true),
    snapshotsAllowed: p.admin_snapshots_allowed != null
      ? (p.admin_snapshots_allowed === true)
      : adminAllowed,
    reReadAllowed: (p.re_read_allowed === true),
    /* Fail-closed Soft theatre: only Live true (never invent on missing). */
    softTheatreLocalOnly: p.soft_theatre_local_only === true,
    armed: (p.armed === true),
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    devicePaintMax: Number.isFinite(dMax) && dMax > 0 ? Math.floor(dMax) : 64,
    alarmPaintMax: Number.isFinite(aMax) && aMax > 0 ? Math.floor(aMax) : 48,
  };
}

/** Pure: flatten GET /devices for overview KPIs / roster — no Soft fill. */
export function overviewDevicesOf(pack) {
  const items = (pack && (pack.items || pack.devices)) || [];
  const rows = Array.isArray(items) ? items : [];
  const mapped = rows
    .map((r) => {
      if (!r) return null;
      const id = String(r.device_id || r.id || "").trim();
      if (!id) return null;
      const online = !!(
        r.is_online === true
        || r.online === true
        || r.presence === "online"
        || String(r.status || "").toLowerCase() === "online"
      );
      const kind = String(r.kind || r.device_kind || "device").toLowerCase() || "device";
      return {
        id,
        label: boardLabelSay(r, id),
        kind,
        zone: String(r.zone || "").trim(),
        offline: !online,
      };
    })
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

const KIND_LABEL = {
  person: "Foot",
  device: "Edge",
  drone: "UAV",
  robot: "UGV",
  camera: "Cam",
  siren: "Siren",
};

/** Soft mobile kinds only — Live edge hardware is fixed / not dispatchable. */
const DISPATCH_MOBILE_KINDS = new Set(["person", "drone", "robot"]);

/**
 * Pure: is this asset allowed on the Dispatchable roster?
 * Fixed Soft cams/sirens and Live edge register stay off the move board.
 */
export function isDispatchMobileAsset(a) {
  if (!a || typeof a !== "object") return false;
  if (a.fixed === true || a.fixed === 1) return false;
  const kind = String(a.kind || "").toLowerCase();
  if (kind === "device" || kind === "camera" || kind === "siren" || kind === "gateway"
    || kind === "sensor" || kind === "contact" || kind === "lidar" || kind === "node") {
    return false;
  }
  const profile = String(a.routeProfile || "").toLowerCase();
  if (profile === "static") return false;
  const speed = Number(a.speed);
  if (Number.isFinite(speed) && speed <= 0) return false;
  return DISPATCH_MOBILE_KINDS.has(kind);
}

/**
 * Pure: orderable **dispatchable** roster (units that can move).
 * Soft: free first, then tasked · person / UAV / UGV only.
 * Live edge hardware is never listed as dispatchable (fixed plant · no free invent).
 */
export function overviewDispatchRosterOf(fleetState, liveDevices, offlineIds) {
  void liveDevices;
  void offlineIds;
  const softAssets = fleetState && Array.isArray(fleetState.assets) ? fleetState.assets : null;
  if (!softAssets || !softAssets.length) {
    /* Live register is fixed plant — Soft invent “online = ready to dispatch” deleted. */
    return [];
  }
  const rows = softAssets.filter(isDispatchMobileAsset).map((a) => {
    const free = !!a.free;
    const how = free
      ? "ready"
      : a.etaS > 0
        ? "enroute"
        : a.missionId
          ? "assigned"
          : "busy";
    const name = String(a.label || "").trim();
    return {
      id: String(a.id || ""),
      label: name && !looksLikeCode(name) ? name : "Someone",
      kind: String(a.kind || "unit"),
      kindWord: KIND_LABEL[a.kind] || "Someone",
      skills: Array.isArray(a.skills) ? a.skills : [],
      free,
      how,
      meta:
        free
          ? (a.skills || []).slice(0, 3).map((s) => operatorSay(s, "")).filter(Boolean).join(" · ") || "Ready"
          : a.etaS
            ? `On the way · ${Math.round(a.etaS)}s`
            : "On a job",
      tone: a.color || "",
      soft: true,
      mobile: true,
    };
  });
  rows.sort((a, b) => Number(b.free) - Number(a.free) || a.label.localeCompare(b.label));
  return rows;
}

function fmtRemain(ms) {
  const n = Math.max(0, Math.ceil(Number(ms) / 1000) || 0);
  const m = Math.floor(n / 60);
  const s = n % 60;
  return m + ":" + String(s).padStart(2, "0");
}

/** Collapsible row: one-line summary always; body only when open. */
function CmdFold({ k, title, sum, badge, badgeData, open, onToggle, children, tone }) {
  const expanded = !!open;
  return html`
    <section
      className=${"ops-cmd__foldcard" + (expanded ? " is-open" : "")}
      data-tone=${tone || ""}
      data-open=${expanded ? "1" : "0"}>
      <button
        type="button"
        className="ops-cmd__foldcard-h"
        aria-expanded=${expanded ? "true" : "false"}
        onClick=${onToggle}>
        <span className="ops-cmd__foldcard-main">
          <p className="ops-cmd__k">${k}</p>
          <h3>
            ${title}
            ${sum ? html`<em> · ${sum}</em>` : null}
          </h3>
        </span>
        <span className="ops-cmd__foldcard-end">
          ${badge
            ? html`<span className="ops-cmd__badge" data-stage=${badgeData || ""} data-mode=${badgeData || ""} data-run=${badgeData || ""}>${badge}</span>`
            : null}
          <i className="ops-cmd__chev" aria-hidden="true">${expanded ? "▾" : "▸"}</i>
        </span>
      </button>
      ${expanded ? html`<div className="ops-cmd__foldcard-b">${children}</div>` : null}
    </section>
  `;
}

/** Pure: Overview KPI strip from site atoms. */
export function overviewKpisOf(site) {
  const s = site && typeof site === "object" ? site : {};
  const devices = Array.isArray(s.devices) ? s.devices : [];
  const offline = Array.isArray(s.offline) ? s.offline : [];
  const alarms = Array.isArray(s.alarms) ? s.alarms : [];
  const total = devices.length;
  const online = Math.max(0, total - offline.length);
  const zones = new Set(devices.map((d) => d && d.zone).filter(Boolean)).size;
  const soft = s.soft || {};
  return {
    total,
    online,
    openAlarms: alarms.length,
    zones,
    devicesReady: !!s.devicesReady,
    beliefConf: s.belief && s.belief.conf != null ? s.belief.conf : null,
    softIntents: soft.intents != null ? soft.intents : UNKNOWN,
    softPlans: soft.plans != null ? soft.plans : UNKNOWN,
    softBound: soft.bound != null ? soft.bound : 0,
  };
}

/** Map admin snapshot rows into thumbs / lightbox shape.
 * Do not unique On-site frames by device_id — two cameras on one board stay two chips.
 */
export function overviewShotsOf(payload) {
  const rows = (payload && payload.items) || [];
  return rows
    .map((r, i) => {
      const gone = !!r.is_expired || !r.has_image;
      const at = r.created_at ? formatUserInstant(r.created_at, { date: false }) : "—";
      return {
        id: String(r.snapshot_id || ""),
        cam: cameraPlantSay(r, i),
        at,
        ttl: r.remaining_label || (r.seconds_remaining != null ? `${r.seconds_remaining}s` : "—"),
        gone,
        hasImage: !!r.has_image && !r.is_expired,
        thumbUrl: r.thumb_url || null,
        deviceId: String(r.device_id || ""),
      };
    })
    .filter((s) => s.id);
}

function _AiCommandStrip({ onSelect, toast, focusDev, devices: devicesProp, offline: offlineProp }) {
  const home = useDecisionHome();
  const live = useLive();
  /* Fail-closed: parent register only. Shared live bus is not a fleet SoT. */
  const devices = Array.isArray(devicesProp) ? devicesProp : [];
  const offline = Array.isArray(offlineProp)
    ? offlineProp
    : ((live && live.offline) || []);
  const [pathOpen, setPathOpen] = useState(false);
  const maic = home.maic || {};
  const pulse = home.pulse || "watching";
  const stage =
    home.aiOn === false
      ? "off"
      : pulse === "needs_you" || home.waiting
        ? "needs"
        : pulse === "acting"
          ? "acting"
          : "watch";
  const stageLabel =
    stage === "needs"
      ? "Needs you"
      : stage === "acting"
        ? "AI working"
        : stage === "off"
          ? "AI off"
          : "AI watching";
  const risk =
    maic.risk_score == null || maic.risk_score === ""
      ? null
      : Math.round(Number(maic.risk_score));
  const riskIndependent = maic.risk_independent === true;
  const waitingN = Number(maic.waiting_n || 0);
  const pathDevice =
    focusDev ||
    (maic.device_id && devices.find((d) => d && d.id === maic.device_id)) ||
    devices[0] ||
    null;
  const handsFree = stage === "watch" || stage === "acting";
  /* Expand when HITL needs you; otherwise start folded. */
  const [open, setOpen] = useState(stage === "needs");
  useEffect(() => {
    if (stage === "needs") setOpen(true);
  }, [stage]);

  const sum =
    waitingN > 0
      ? waitingN + " waiting"
      : handsFree
        ? "hands-free"
        : stage === "off"
          ? "off"
          : "operator";

  return html`
    <${CmdFold}
      k="Watch · On site"
      title=${stageLabel}
      sum=${sum}
      badge=${stage === "needs" ? "NEED" : stage === "off" ? "OFF" : "AI"}
      badgeData=${stage}
      tone=${stage === "needs" ? "needs" : handsFree ? "ai" : ""}
      open=${open}
      onToggle=${() => setOpen((v) => !v)}>
      <div
        className="ops-cmd__ai ops-cmd__ai--in"
        data-stage=${stage}
        data-handsfree=${handsFree ? "1" : "0"}
        data-cmc-ai="page41"
        data-cmc-page="page64-hitl">
        <p className="ops-cmd__why">
          ${home.why ||
          (stage === "off"
            ? "AI is off on this site."
              : handsFree
              ? "No one needed · the Watch runs until it asks you."
              : "On-site AI context for this seat · lab session never pretends to be on site.")}
        </p>
        ${maic.device_id || (risk != null && Number.isFinite(risk))
          ? html`
              <div className="ops-cmd__kv">
                ${maic.device_id
                  ? html`<button
                      type="button"
                      className="ops-cmd__kv-btn"
                      onClick=${() => onSelect && onSelect(maic.device_id)}>
                      <i>Focus</i><b>${boardHeadingOf(maic.device_id)}</b>
                    </button>`
                  : null}
                ${risk != null && Number.isFinite(risk)
                  ? (riskIndependent
                    ? html`<span><i>Risk</i><b>${risk}</b></span>`
                    : html`<span data-cmc-risk="unproven"><i>Risk</i><b>unproven</b></span>`)
                  : null}
              </div>
            `
          : null}
        ${home.actions.length
          ? html`
              <div className="ops-cmd__acts">
                ${home.actions.map(
                  (a) => html`
                    <button
                      key=${a.id}
                      type="button"
                      className=${"btn btn--sm" + (a.id === "continue" || a.id === "approve" ? " btn--go" : " btn--ghost")}
                      disabled=${home.busy}
                      onClick=${() => home.act(a, toast)}>
                      ${actionLabel(a)}
                    </button>
                  `,
                )}
              </div>
            `
          : null}
        ${!home.actions.length && home.ctx && home.ctx.error
          ? html`<${Alert}
              tone="warn"
              title="Home-context unavailable"
              say=${String(home.ctx.error)}
            />`
          : null}
        <button
          type="button"
          className="ops-cmd__fold"
          data-on=${pathOpen ? "1" : "0"}
          onClick=${() => setPathOpen((v) => !v)}>
          ${pathOpen ? "Hide path / cutseal" : "Path / seat"}
        </button>
        ${pathOpen
          ? html`<div className="ops-cmd__path">
              <${PathsealPanel}
                device=${pathDevice}
                offline=${offline}
                onSelect=${onSelect}
                toast=${toast}
              />
            </div>`
          : null}
      </div>
    </${CmdFold}>
  `;
}

function _SoftMapDirector({ ledger }) {
  const [snap, setSnap] = useState(null);
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  useEffect(() => {
    const poll = () => {
      try {
        setSnap((typeof window !== "undefined" && window.__crocSoftDir) || null);
      } catch {
        setSnap(null);
      }
    };
    poll();
    const id = setInterval(poll, 900);
    return () => clearInterval(id);
  }, []);
  const mode = (snap && snap.mode) || "off";
  const remain = snap && snap.remainMs != null ? snap.remainMs : 0;
  const focus = snap && snap.focus;
  const say = (snap && snap.say) || "Human camera · Soft fleet only";
  const modeWord =
    mode === "ai"
      ? "AI tour"
      : mode === "human-pin"
        ? "Pin hold"
        : mode === "human"
          ? "Human"
          : "—";
  const rows = Array.isArray(ledger) ? ledger.slice(0, 5) : [];
  useEffect(() => {
    if (mode === "ai") setOpen(true);
  }, [mode]);

  const sum =
    mode === "ai" && focus
      ? (focus.label || focus.id || "unit") +
        (snap && snap.targets ? ` · ${(snap.tourIndex || 0) + 1}/${snap.targets}` : "")
      : mode === "human" || mode === "human-pin"
        ? "AI in " + fmtRemain(remain)
        : "Quiet";

  return html`
    <${CmdFold}
      k="Map · AI director"
      title=${modeWord}
      sum=${sum}
      badge=${mode === "ai" ? "AI" : "YOU"}
      badgeData=${mode}
      tone=${mode === "ai" ? "ai" : "own"}
      open=${open}
      onToggle=${() => setOpen((v) => !v)}>
      <div className="ops-cmd__sup-body" data-mode=${mode} data-own="1">
        <p className="ops-cmd__why">${say}</p>
        ${mode === "ai" && snap && snap.targets
          ? html`
              <div className="ops-cmd__kv">
                <span><i>Tour</i><b>${(snap.tourIndex || 0) + 1}/${snap.targets}</b></span>
                ${snap.dwellLeftMs != null
                  ? html`<span><i>Dwell</i><b>${fmtRemain(snap.dwellLeftMs)}</b></span>`
                  : null}
                ${focus && focus.how
                  ? html`<span><i>How</i><b>${focus.how}</b></span>`
                  : null}
              </div>
            `
          : null}
        <button
          type="button"
          className="ops-cmd__fold"
          data-on=${reportOpen ? "1" : "0"}
          onClick=${() => setReportOpen((v) => !v)}>
          ${reportOpen
            ? "Hide dispatch report"
            : `Dispatch report · ${rows.length ? rows.length + " events" : "empty"} · own fleet`}
        </button>
        ${reportOpen
          ? html`
              <div className="ops-cmd__report" aria-label="Dispatch report">
                ${!rows.length
                  ? html`<p className="ops-cmd__report-empty">No dispatch notes yet.</p>`
                  : html`
                      <ul className="ops-cmd__report-list">
                        ${rows.map(
                          (r) => html`
                            <li key=${r.id || r.ts + r.kind}>
                              <time>${r.ts || "—"}</time>
                              <b>${operatorSay(r.kind, "Event")}</b>
                              <span>${r.say || ""}</span>
                            </li>
                          `,
                        )}
                      </ul>
                    `}
              </div>
            `
          : null}
      </div>
    </${CmdFold}>
  `;
}

function _JobNow({ fleet, belief }) {
  const localSoft = isLocalHost();
  const lead =
    fleet && fleet.state && Array.isArray(fleet.state.missions) && fleet.state.missions[0]
      ? fleet.state.missions[0]
      : null;
  const planning = !!(fleet && fleet.state && fleet.state.planning);
  const active = !!(localSoft && (lead || planning)) || !!belief;
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active, lead && lead.id]);

  if (localSoft && (lead || planning)) {
    const assigned = (lead && lead.assigned) || [];
    const freeN =
      fleet.state && Array.isArray(fleet.state.assets)
        ? fleet.state.assets.filter((a) => a.free).length
        : 0;
    const sum = lead
      ? [
          lead.place || null,
          lead.maxEta != null ? "η " + lead.maxEta + "s" : null,
          assigned.length + " out",
        ]
          .filter(Boolean)
          .join(" · ")
      : "routing…";
    return html`
      <${CmdFold}
        k="Soft job · theatre"
        title=${lead ? lead.title : "Planning…"}
        sum=${sum}
        badge=${planning ? "routing" : lead.status || "active"}
        badgeData=${planning ? "0" : "1"}
        tone="soft"
        open=${open}
        onToggle=${() => setOpen((v) => !v)}>
        <div className="ops-cmd__job ops-cmd__job--in" data-soft="1">
          <p className="ops-cmd__job-m">
            ${lead
              ? [lead.place, `${assigned.length} committed`, `${freeN} ready`].filter(Boolean).join(" · ")
              : "Skill match on roads / air"}
          </p>
          ${lead && lead.needs && lead.needs.length
            ? html`
                <div className="ops-cmd__skills" aria-label="Skills">
                  ${lead.needs.map((n) => {
                    const cover =
                      lead.skillCover && lead.skillCover.find((c) => c.id === n && c.covered);
                    return html`<span key=${n} data-cover=${cover ? "1" : "0"}>${n}</span>`;
                  })}
                </div>
              `
            : null}
        </div>
      </${CmdFold}>
    `;
  }

  if (belief) {
    return html`
      <${CmdFold}
        k="Runtime belief"
        title=${belief.label}
        sum=${belief.src + " · " + belief.conf + "%"}
        badge=${belief.conf + "%"}
        badgeData="needs"
        open=${open}
        onToggle=${() => setOpen((v) => !v)}>
        <div className="ops-cmd__job ops-cmd__job--in" data-soft="0">
          <button type="button" className="ops-cmd__job-go" onClick=${() => go("/response/cases")}>
            <span>Open Mission Room · rule ${belief.rules}</span>
          </button>
        </div>
      </${CmdFold}>
    `;
  }

  return html`
    <${CmdFold}
      k="Job"
      title="No active job"
      sum=${localSoft ? "Soft next" : "quiet"}
      open=${open}
      onToggle=${() => setOpen((v) => !v)}>
      <p className="ops-cmd__job-m">
        ${localSoft
          ? "Next job is ready"
          : "Watch is quiet — only confirmed buses on this host"}
      </p>
    </${CmdFold}>
  `;
}

function _DispatchRoster({ rows, onSelect, soft }) {
  const ready = rows.filter((r) => r.free);
  const busy = rows.filter((r) => !r.free);
  const _filter = soft ? "Demo mobile · fixed boards off" : "Fixed boards · not dispatchable";

  return html`
    <section className="ops-cmd__roster ops-cmd__roster--link" aria-label="Dispatchable units">
      <button
        type="button"
        className="ops-cmd__roster-h ops-cmd__roster-go"
        onClick=${() => go(soft ? "/ops/workbench" : "/ops/dispatch")}>
        <div>
          <p className="ops-cmd__k">Dispatchable · mobile only</p>
          <h3>
            ${ready.length} ready
            <em> · ${busy.length} committed</em>
          </h3>
        </div>
        <span className="ops-cmd__filter">${soft ? "Command ▸" : "Roster ▸"}</span>
      </button>
      <div className="ops-cmd__roster-list" role="list">
        ${!rows.length
          ? html`
              <div className="ops-cmd__empty">
                <b>${soft ? "No mobile Soft units" : "Hardware is fixed"}</b>
                <p>${soft
                  ? "Foot / UAV / UGV only on the dispatch board."
                  : "Fixed boards stay put. Mobile units only appear on the run board."}</p>
              </div>
            `
          : rows.slice(0, 4).map(
              (r) => html`
                <button
                  key=${r.id}
                  type="button"
                  role="listitem"
                  className="ops-cmd__unit"
                  data-how=${r.how}
                  data-free=${r.free ? "1" : "0"}
                  data-soft=${r.soft ? "1" : "0"}
                  data-mobile=${r.mobile ? "1" : "0"}
                  style=${r.tone ? { "--unit": r.tone } : null}
                  onClick=${() => {
                    if (r.soft) {
                      try {
                        window.dispatchEvent(
                          new CustomEvent("croc-soft-focus", { detail: { id: r.id } }),
                        );
                      } catch {
                        /* */
                      }
                      return;
                    }
                    if (onSelect) onSelect(r.id);
                  }}>
                  <span className="ops-cmd__unit-kind">${r.kindWord}</span>
                  <span className="ops-cmd__unit-main">
                    <b>${r.label}</b>
                    <i>${r.meta || ""}</i>
                  </span>
                  <span className="ops-cmd__unit-st">${r.free ? "Free" : operatorSay(r.how, "Busy")}</span>
                </button>
              `,
            )}
        ${rows.length > 4
          ? html`
              <button
                type="button"
                className="ops-cmd__fold"
                onClick=${() => go(soft ? "/ops/workbench" : "/ops/dispatch")}>
                +${rows.length - 4} more · open Dispatch desk
              </button>
            `
          : null}
      </div>
    </section>
  `;
}

function FootFrames({ onShot, allowed }) {
  const r = useApi(
    allowed ? safePath("get__admin_camera-snapshots", { query: { limit: 12 } }) : "",
  );
  if (!allowed) return null;
  const shots = overviewShotsOf(r.data).filter((s) => !s.gone).slice(0, 2);
  if (!shots.length) return null;
  return html`
    <div className="ops-cmd__frames">
      ${shots.map(
        (sn) => html`
          <button key=${sn.id} type="button" className="ops-cmd__frame" onClick=${() => onShot(sn)}>
            <u>${sn.cam}</u><s>${sn.at}</s>
          </button>
        `,
      )}
    </div>
  `;
}

export function OpsOverviewDesk({
  onSelect,
  onShot,
  toast,
  selected,
  fleet,
  activeZoneId,
  onEnterZone,
  onClearZone,
}) {
  const posture = useApi(safePath("get__ops_overview_status"));
  const post = posture.data || {};
  const seat = opsOverviewSeatOf(
    posture.loading || !posture.data
      ? {
          enabled: true,
          armed: false,
          survey_plane_allowed: false,
          admin_plane_allowed: false,
          register_allowed: false,
          re_read_allowed: false,
          soft_theatre_local_only: true,
          limits: { device_paint_max: 64, alarm_paint_max: 48 },
        }
      : post,
  );
  const devicesR = useApi(seat.registerAllowed ? safePath("get__devices") : "");
  const alarmsR = useApi(
    seat.alarmsAllowed
      ? safePath("get__alarms", { query: { since_hours: 24, limit: 100 } })
      : "",
  );
  /* Shared live still supplies belief / soft-lane chips / pathseal offline list. */
  const live = useLive();
  const homeWatch = useDecisionHome();
  const watchPulse = homeWatch.pulse || "watching";
  const localSoft = isLocalHost() && seat.softTheatreLocalOnly;
  const regDevices = useMemo(() => overviewDevicesOf(devicesR.data), [devicesR.data]);
  const regReady = seat.registerAllowed
    ? (!!devicesR.data || !!devicesR.error) && !devicesR.loading
    : !posture.loading;
  /* Fail-closed: loading/error never invents a fleet from the shared live bus. */
  const devices = regReady ? regDevices : [];
  const offline = devices.filter((d) => d && d.offline).map((d) => d.id);
  /* Fail-closed: sealed/error alarm seat stays empty — shared live bus is not an alarm SoT here. */
  const alarmsReady = seat.alarmsAllowed
    ? (!!alarmsR.data || !!alarmsR.error) && !alarmsR.loading
    : false;
  const alarms = alarmsReady && alarmsR.data && !alarmsR.error
    ? (alarmsR.data.items || alarmsR.data.alarms || [])
    : [];
  const site = {
    devices,
    offline,
    alarms: Array.isArray(alarms) ? alarms : [],
    zones: (live && live.zones) || [],
    belief: live && live.belief,
    soft: (live && live.soft) || {},
    ops: (live && live.ops) || { notify: UNKNOWN, siren: UNKNOWN, sirenTone: "" },
    devicesReady: regReady,
  };
  const kpis = overviewKpisOf(site);
  const total = kpis.total;
  const online = kpis.online;
  const selId =
    typeof selected === "string" || typeof selected === "number"
      ? String(selected)
      : selected && selected.id
        ? String(selected.id)
        : "";
  const _focusDev = selId ? site.devices.find((d) => d && String(d.id) === selId) : null;

  const softState = localSoft && fleet && fleet.enabled ? fleet.state : null;
  const roster = overviewDispatchRosterOf(softState, site.devices, site.offline);

  const volumeDeck = useMemo(() => {
    if (!localSoft) {
      return (site.zones || []).slice(0, 4).map((z) => ({
        id: z.id || z.label,
        label: z.label || z.id,
        soft: false,
        coverN: 0,
        plantN: z.count != null ? z.count : (z.deviceIds || []).length,
        ready: "open",
        readyLabel: "On site",
        roleLabel: "Ring",
        tone: "aqua",
      }));
    }
    const binds = loadZoneCamBinds();
    const cat = softZoneCatalogOf({
      zones: site.zones || [],
      devices: site.devices,
      softState,
      centre: softState && softState.origin,
    });
    return cat.map((z) => {
      const cams = Array.isArray(binds[z.id]) ? binds[z.id] : z.cameraIds || [];
      const n = cams.length;
      return {
        ...z,
        cameraIds: cams,
        coverN: n,
        ready: n >= 2 ? "ready" : n === 1 ? "thin" : "open",
        readyLabel: n >= 2 ? "Covered" : n === 1 ? "Thin" : "Open",
      };
    });
  }, [localSoft, softState, site.zones, site.devices]);

  const enterVol = (id) => {
    if (onEnterZone) onEnterZone(id);
    else go("/ops/zones");
  };

  const reRead = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh sealed", "warn");
      return;
    }
    posture.reload && posture.reload();
    devicesR.reload && devicesR.reload();
    alarmsR.reload && alarmsR.reload();
    if (live && typeof live.reload === "function") live.reload();
    homeWatch.reload && homeWatch.reload();
    toast && toast("Refreshed", "go");
  };

  if (!posture.loading && post.enabled === false) {
    return html`<div
      className="ops-sit ops-sit--field ops-sit--v390 ops-ov-desk ops-ov-desk--liquid"
      data-cmc-page="page144-overview"
      data-cmc-legacy="page64-overview"
      data-cmc-residual="page220-overview"
      data-field="v390"
      data-soft="0">
      <header className="ops-sit__head">
        <h2 className="ops-sit__title">Seat closed</h2>
        <p className="ops-sit__pulse">Map posture refused</p>
      </header>
      <button type="button" className="btn btn--ghost btn--sm" disabled=${!seat.reReadAllowed} onClick=${reRead}>
        <${Ico} d="refresh" w=${13} /> Re-read
      </button>
    </div>`;
  }

  const readyN = roster.filter((r) => r.free).length;
  const focusHot = !!(homeWatch.waiting || watchPulse === "needs_you" || watchPulse === "acting");
  const spaceList = volumeDeck.slice(0, 6);
  const unitList = roster.slice(0, 8);
  const coverPct = (n) => Math.min(100, Math.round(((Number(n) || 0) / 3) * 100));
  const plantV = !seat.surveyAllowed
    ? "—"
    : kpis.devicesReady
      ? total
        ? `${online}/${total}`
        : "0"
      : "…";

  return html`<div
    className="ops-field ops-field--now ops-sit ops-sit--field ops-hud ops-ov-desk ops-ov-desk--theatre"
    data-cmc-page="page144-overview"
    data-cmc-legacy="page64-overview"
    data-cmc-residual="page220-overview"
    data-cmc-soft-real="alarms-seat-only"
    data-field="v550"
    data-soft=${localSoft ? "1" : "0"}
    data-hot=${focusHot ? "1" : "0"}>

    <div className="ops-hud__scan" aria-hidden="true" />
    <div className="ops-hud__vignette" aria-hidden="true" />

    <${M.header}
      className="ops-hud__top"
      initial=${{ opacity: 0, y: -8 }}
      animate=${{ opacity: 1, y: 0 }}
      transition=${SOFT}>
      <div className="ops-hud__brand">
        <span className="ops-hud__chip">Now</span>
        ${localSoft
          ? html`<span className="ops-hud__chip ops-hud__chip--soft">Lab only</span>`
          : html`<span className="ops-hud__chip ops-hud__chip--live">On site</span>`}
      </div>
      <${M.button}
        type="button"
        className="ops-hud__refresh"
        title="Refresh these numbers"
        disabled=${!seat.reReadAllowed && !posture.loading}
        whileHover=${{ rotate: 40 }}
        whileTap=${{ scale: 0.9, rotate: 180 }}
        transition=${SPRING}
        onClick=${reRead}>
        <${Ico} d="refresh" w=${13}/>
      <//>
    <//>

    <${TonightWatchCmc} home=${homeWatch} toast=${toast} />

    <div className="ops-hud__meters" role="group" aria-label="Site meters">
      ${[
        { k: "Run", v: watchPulse === "needs_you" ? "Need you" : "Open", on: watchPulse === "needs_you" || watchPulse === "acting", tone: watchPulse === "needs_you" ? "warn" : "go", href: "/ops/workbench", title: watchPulse === "needs_you" ? "Needs You" : "Open Run" },
        { k: "Free", v: readyN, on: !!readyN, tone: "go", href: "/ops/dispatch" },
        { k: "Boards", v: plantV, on: !!(seat.surveyAllowed && total && online === total), tone: "go", href: "/ops/devices", title: total ? `${online} of ${total} boards up${kpis.openAlarms ? ` · ${kpis.openAlarms} signals as evidence` : ""}` : "Watch is quiet" },
        { k: "Armed", v: seat.armed ? "Yes" : "No", on: !!seat.armed, tone: "go" },
      ].map(
        (m, i) => html`
          <${M.button}
            key=${m.k}
            type="button"
            className=${"ops-hud__meter" + (m.k === "Run" ? " ops-hud__cta" : "")}
            data-on=${m.on ? "1" : "0"}
            data-tone=${m.tone}
            disabled=${!m.href}
            title=${m.title || (m.href ? "Open " + m.k : "Armed state")}
            initial=${{ opacity: 0, y: 10, scale: 0.94 }}
            animate=${{ opacity: 1, y: 0, scale: 1 }}
            whileHover=${m.href ? { y: -2, scale: 1.02 } : {}}
            transition=${{ ...SOFT, delay: 0.04 * i }}
            onClick=${() => m.href && go(m.href)}>
            <b>${m.v}</b>
            <em>${m.k}</em>
            <s className="ops-hud__meter-bar" aria-hidden="true"
              style=${{ transform: `scaleX(${m.on ? 1 : 0.22})` }}/>
          <//>`,
      )}
    </div>

    <div className="ops-hud__body">
      <section className="ops-hud__lane" aria-label="Areas">
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Areas</p>
          <button type="button" className="btn btn--text btn--xs" onClick=${() => go("/ops/zones")}>All</button>
        </div>
        <div className="ops-hud__vols" role="list">
          ${!spaceList.length
            ? html`<p className="ops-hud__quiet">No areas yet · <button type="button" className="btn btn--text btn--xs" data-cmc-draw-zone="1" onClick=${() => go("/places/zones")}>Draw a zone</button></p>`
            : spaceList.map(
                (z, i) => html`
                  <${M.button}
                    key=${z.id}
                    type="button"
                    role="listitem"
                    className="ops-hud__vol"
                    data-on=${activeZoneId === z.id ? "1" : "0"}
                    data-ready=${z.ready || "open"}
                    title=${"Focus " + (z.label || z.id)}
                    initial=${{ opacity: 0, y: 12, scale: 0.96 }}
                    animate=${{ opacity: 1, y: 0, scale: 1 }}
                    whileHover=${{ y: -3, scale: 1.02 }}
                    whileTap=${{ scale: 0.97 }}
                    transition=${{ ...SOFT, delay: Math.min(0.14, 0.04 * i) }}
                    onClick=${() => enterVol(z.id)}>
                    <span className="ops-hud__vol-role">${z.roleLabel || "Area"}</span>
                    <b className="ops-hud__vol-name">${z.label}</b>
                    <span className="ops-hud__charge" aria-hidden="true">
                      <i style=${{ width: `${coverPct(z.coverN)}%` }} />
                    </span>
                    <u className="ops-hud__vol-meta">
                      ${z.readyLabel || "Open"}${z.coverN != null ? " · " + z.coverN + " cam" : ""}
                    </u>
                  <//>`,
              )}
        </div>
      </section>

      <section className="ops-hud__lane" aria-label="People">
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>People</p>
          <button type="button" className="btn btn--text btn--xs"
            onClick=${() => go(softState ? "/ops/workbench" : "/ops/dispatch")}>
            ${softState ? "Run" : "Send"}
          </button>
        </div>
        <div className="ops-hud__squad" role="list">
          ${!unitList.length
            ? html`<p className="ops-hud__quiet">${localSoft ? "No people on the practice map" : "Nobody to send yet"}</p>`
            : unitList.map(
                (r, i) => html`
                  <${M.button}
                    key=${r.id}
                    type="button"
                    role="listitem"
                    className="ops-hud__unit"
                    data-free=${r.free ? "1" : "0"}
                    title=${r.free ? "Focus · open Run" : "Busy"}
                    initial=${{ opacity: 0, scale: 0.9 }}
                    animate=${{ opacity: 1, scale: 1 }}
                    whileHover=${{ y: -2, scale: 1.04 }}
                    whileTap=${{ scale: 0.96 }}
                    transition=${{ ...SOFT, delay: Math.min(0.16, 0.035 * i) }}
                    onClick=${() => {
                      if (softState) {
                        try {
                          window.dispatchEvent(
                            new CustomEvent("croc-soft-focus", { detail: { id: r.id } }),
                          );
                        } catch {
                          /* */
                        }
                        go("/ops/workbench");
                        return;
                      }
                      onSelect && onSelect(r.id || r);
                    }}>
                    <i className="ops-hud__unit-dot" aria-hidden="true" />
                    <b>${r.label || r.id}</b>
                    <em>${r.free ? "Free" : "Busy"}</em>
                  <//>`,
              )}
        </div>
      </section>
    </div>

    <footer className="ops-hud__foot">
      <span className="ops-hud__foot-role" data-soft=${localSoft ? "1" : "0"}>
        ${localSoft ? "Lab seat" : "On site"}
      </span>
      ${seat.snapshotsAllowed && seat.adminAllowed
        ? html`<div className="ops-hud__frames"><${FootFrames} onShot=${onShot} allowed=${true}/></div>`
        : null}
    </footer>
  </div>`;
}
